import { computed, Injectable, OnDestroy, signal } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { Message } from '@app/shared/models/app.model';

import { MessageBuffer } from './internals/message-buffer';
import { MessageState } from './internals/message-state';

import { TypingQueue } from './internals/typing-queue';

function parseWords(text: string): string[] {
  if (!text) return [];
  return text.split(/\s+/).filter(Boolean);
}

const DEFAULT_TYPING_SPEED_MS = 170;
const DEFAULT_TYPING_VARIANCE_MS = 30;
const STATE_REMOVAL_DELAY_MS = 500;
const POST_RELEASE_DELAY_MS = 2000;

/**
 * Orchestrates the translation delay pipeline: buffer → release → type → freeze.
 * Incoming messages are held in a buffer (allowing translations to stabilize),
 * then released into a typing queue that reveals words one-by-one.
 */
@Injectable()
export class StableTextService implements OnDestroy {
  private states = new Map<string, MessageState>();
  private buffer: MessageBuffer;
  private queue: TypingQueue;
  private pendingRemovals = new Map<string, ReturnType<typeof setTimeout>>();
  private postReleaseTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private currentBufferMs = 0;
  private onVisibilityChange = () => {
    if (!document.hidden) this.flushPendingWork();
  };

  private _retainedMessages = signal<Message[]>([]);
  readonly retainedMessages = this._retainedMessages.asReadonly();

  private _bufferedDocumentIds = signal<Set<string>>(new Set());
  readonly delayedDocumentIds = computed(() => {
    const retained = this._retainedMessages();
    const buffered = this._bufferedDocumentIds();
    const delayed = new Set<string>();
    for (const msg of retained) {
      if (buffered.has(msg.documentID)) {
        delayed.add(msg.documentID);
      }
    }
    return delayed;
  });

  /**
   * Messages ready to display: retained messages minus any still inside their
   * translation buffer. Messages are not held back behind an earlier one that is
   * still typing (that would hide finished messages); each types independently.
   */
  readonly displayReadyMessages = computed(() => {
    const retained = this._retainedMessages();
    const delayed = this.delayedDocumentIds();

    return delayed.size > 0
      ? retained.filter(m => !delayed.has(m.documentID))
      : retained;
  });

  // Already-typed text kept briefly after a message is removed, so a quick
  // remove/re-add (retention churn, re-mount, isDelayActive branch flip)
  // restores it instead of re-typing the whole message from scratch.
  private committedTextCache = new Map<string, { text: string; isFinal: boolean }>();
  private readonly MAX_CACHED_COMMITTED = 60;

  private pendingWorkStreams = new Map<string, BehaviorSubject<boolean>>();

  constructor() {
    this.buffer = new MessageBuffer(
      0,
      (docId, text, isFinal) => this.onBufferRelease(docId, text, isFinal)
    );

    this.queue = new TypingQueue(
      (docId, index, word) => this.onWordTyped(docId, index, word),
      (docId) => this.onMessageComplete(docId),
      DEFAULT_TYPING_SPEED_MS,
      DEFAULT_TYPING_VARIANCE_MS
    );

    // Flush pending typing work when tab returns to foreground to avoid
    // catch-up lag from setTimeout throttling in background tabs
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    this.reset();
  }

  /** Clears all internal state. Call when the consuming view is torn down. */
  reset(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    for (const timeout of this.pendingRemovals.values()) clearTimeout(timeout);
    this.pendingRemovals.clear();

    for (const timer of this.postReleaseTimers.values()) clearTimeout(timer);
    this.postReleaseTimers.clear();

    this.buffer.destroy();
    this.queue.destroy();

    for (const state of this.states.values()) state.destroy();
    this.states.clear();

    for (const subject of this.pendingWorkStreams.values()) {
      subject.next(false);
      subject.complete();
    }
    this.pendingWorkStreams.clear();

    this.committedTextCache.clear();

    this._retainedMessages.set([]);
    this._bufferedDocumentIds.set(new Set());
  }

  /**
   * Instantly commits all words for a message (no typing animation).
   * Used for pre-existing messages when delay activates mid-session.
   */
  commitInstantly(documentId: string, text: string, isFinal: boolean): void {
    const state = this.getOrCreateState(documentId);
    if (state.isFrozen) return;

    const words = parseWords(text);
    state.updateRawWords(words);
    if (isFinal) state.markFinal();
    state.markReleased();

    for (let i = 0; i < words.length; i++) {
      state.commitWord(i, words[i]);
    }

    if (isFinal) state.freeze();
    this.updatePendingWorkState(documentId);
  }

  /**
   * Instantly commits all pending work (buffered + typing) without animation.
   * Call when the browser tab returns to foreground to avoid the catch-up lag
   * caused by setTimeout throttling in background tabs.
   */
  private flushPendingWork(): void {
    // 1. Release any messages still waiting in the translation buffer
    this.buffer.releaseAll();

    // 2. Stop the typing queue — no more word-by-word animation
    this.queue.flush();

    // 3. Flush all post-release timers
    for (const [docId, timer] of this.postReleaseTimers) {
      clearTimeout(timer);
    }
    this.postReleaseTimers.clear();

    // 4. For each tracked message, commit all uncommitted words instantly
    for (const [docId, state] of this.states) {
      if (state.isFrozen) continue;

      const uncommitted = state.getUncommittedWords();
      for (const { index, word } of uncommitted) {
        state.commitWord(index, word);
      }

      state.setTyping(false);
      if (state.isFinal && state.isFullyCommitted()) {
        state.freeze();
      }

      this.updatePendingWorkState(docId);
    }
  }

  /** Updates the translation buffer delay (in ms) at runtime. */
  setTranslationBufferMs(ms: number): void {
    this.currentBufferMs = ms;
    this.buffer.setBufferMs(ms);
  }

  // --- Public API ---

  /**
   * Sets the current message array and applies retention logic.
   * Retains all final messages and the most recent non-final per speaker/documentID.
   * The full session is retained (no message-count cap) so users can scroll back
   * through their whole conversation.
   */
  setMessages(messages: Message[]): void {
    if (!messages || !Array.isArray(messages)) {
      return;
    }

    const retained = this.applyRetentionRules(messages);
    const prevRetained = this._retainedMessages();

    // Build lookup maps
    const prevMap = new Map(prevRetained.map(m => [m.documentID, m]));
    const newMap = new Map(retained.map(m => [m.documentID, m]));

    // Update or add messages
    for (const msg of retained) {
      const prev = prevMap.get(msg.documentID);
      if (!prev || prev.message !== msg.message || prev.is_final !== msg.is_final) {
        this.updateMessage(msg.documentID, msg.message, msg.is_final, msg.timestamp);
      }
    }

    // Remove messages no longer in retained set
    for (const prevMsg of prevRetained) {
      if (!newMap.has(prevMsg.documentID)) {
        this.removeMessage(prevMsg.documentID);
      }
    }

    this._retainedMessages.set(retained);
  }

  /**
   * Applies retention rules to incoming messages:
   * 1. Keep all final messages
   * 2. Keep most recent non-final per speaker (micId)
   * 3. Maintain insertion order (by timestamp)
   *
   * No message-count cap is applied: the full conversation is retained so
   * soundshape QR users can scroll back through their entire session. This
   * matches the uncapped non-buffered path (SpeechService `_rawMessages`).
   */
  private applyRetentionRules(messages: Message[]): Message[] {
    if (messages.length === 0) return [];

    // Sort by timestamp to maintain insertion order
    const sorted = [...messages].sort((a, b) => a.timestamp - b.timestamp);

    // Group by micId to find most recent non-final per speaker
    const mostRecentNonFinalByMic = new Map<number | string, Message>();
    for (const msg of sorted) {
      if (!msg.is_final && msg.micId !== undefined) {
        mostRecentNonFinalByMic.set(msg.micId, msg);
      }
    }

    // Build retained set: all finals + most recent non-finals
    const retained: Message[] = [];
    const nonFinalDocIds = new Set([...mostRecentNonFinalByMic.values()].map(m => m.documentID));

    for (const msg of sorted) {
      if (msg.is_final || nonFinalDocIds.has(msg.documentID)) {
        retained.push(msg);
      }
    }

    return retained;
  }

  /** Accepts a message update. Buffers new messages; queues post-release updates for typing. */
  updateMessage(documentId: string, text: string, isFinal: boolean, messageTimestamp?: number): void {
    this.cancelPendingRemoval(documentId);

    let state = this.states.get(documentId);
    if (!state) {
      state = new MessageState(documentId);
      this.states.set(documentId, state);
    }

    // Restore previously-typed text for a message that was just re-added, so it
    // does not visibly rebuild from the first word.
    this.restoreCommittedText(documentId, text);

    if (state.isFrozen) {
      return;
    }

    const words = parseWords(text);
    state.updateRawWords(words);
    if (isFinal) state.markFinal();

    if (state.isReleased) {
      if (isFinal) {
        // Final: flush any pending post-release timer and enqueue immediately
        this.flushPostReleaseTimer(documentId);
        const uncommitted = state.getUncommittedWords();
        if (uncommitted.length > 0) {
          if (!state.isTyping) state.setTyping(true);
          this.queue.enqueue(documentId, uncommitted);
        } else if (state.isTyping) {
          state.setTyping(false);
        }
      } else {
        // Non-final: throttle enqueuing to allow translations to stabilize
        const uncommitted = state.getUncommittedWords();
        if (uncommitted.length > 0) {
          this.schedulePostReleaseEnqueue(documentId);
        } else if (state.isTyping) {
          state.setTyping(false);
        }
      }
      this.updatePendingWorkState(documentId);
      return;
    }

    this.buffer.add(documentId, text, isFinal, messageTimestamp);

    // Only mark as buffered if still pending — with bufferMs=0, buffer.add()
    // releases synchronously via onBufferRelease which already clears the ID
    if (this.buffer.isBuffered(documentId)) {
      const current = new Set(this._bufferedDocumentIds());
      current.add(documentId);
      this._bufferedDocumentIds.set(current);
    }

    this.updatePendingWorkState(documentId);
  }

  getDisplayStream(documentId: string): Observable<string> {
    this.cancelPendingRemoval(documentId);
    return this.getOrCreateState(documentId).displayedText$;
  }

  /**
   * Returns an observable that emits true when the message has pending work
   * (buffering, typing, or uncommitted words) and false when complete.
   * Emits immediately with current state, then on state changes.
   */
  getPendingWorkStream(documentId: string): Observable<boolean> {
    if (!this.pendingWorkStreams.has(documentId)) {
      const initialState = this.hasPendingWork(documentId);
      this.pendingWorkStreams.set(documentId, new BehaviorSubject(initialState));
    }
    return this.pendingWorkStreams.get(documentId)!.pipe(distinctUntilChanged());
  }

  /** Schedules state cleanup after a short delay (prevents flicker on rapid re-renders). */
  removeMessage(documentId: string): void {
    const existing = this.pendingRemovals.get(documentId);
    if (existing) clearTimeout(existing);

    this.pendingRemovals.set(documentId, setTimeout(() => {
      this.pendingRemovals.delete(documentId);
      this.flushPostReleaseTimer(documentId);
      this.buffer.remove(documentId);
      this.queue.remove(documentId);
      const state = this.states.get(documentId);
      if (state) {
        if (state.displayedText) {
          this.cacheCommittedText(documentId, state.displayedText, state.isFinal);
        }
        state.destroy();
        this.states.delete(documentId);
      }

      const buffered = new Set(this._bufferedDocumentIds());
      buffered.delete(documentId);
      this._bufferedDocumentIds.set(buffered);

      const subject = this.pendingWorkStreams.get(documentId);
      if (subject) {
        subject.next(false);
        subject.complete();
        this.pendingWorkStreams.delete(documentId);
      }

    }, STATE_REMOVAL_DELAY_MS));
  }

  /** True if the message is still buffered, typing, or has uncommitted words. */
  hasPendingWork(documentId: string): boolean {
    const state = this.states.get(documentId);
    if (!state) return false;
    if (this.buffer.isBuffered(documentId)) return true;
    if (this.queue.isQueued(documentId)) return true;
    if (!state.isFullyCommitted() && !state.isFrozen) return true;
    return false;
  }

  // --- Internal Handlers ---

  /** Called when the buffer timer expires — transitions the message into the typing phase. */
  private onBufferRelease(documentId: string, text: string, isFinal: boolean): void {
    const state = this.states.get(documentId);
    if (!state) {
      console.warn('[StableText] onBufferRelease: no state for', documentId.substring(0, 8));
      return;
    }
    state.markReleased();

    const current = new Set(this._bufferedDocumentIds());
    current.delete(documentId);
    this._bufferedDocumentIds.set(current);

    state.updateRawWords(parseWords(text));
    if (isFinal) state.markFinal();

    const uncommitted = state.getUncommittedWords();
    if (uncommitted.length > 0) {
      state.setTyping(true);
      this.queue.enqueue(documentId, uncommitted);
    } else if (isFinal) {
      state.freeze();
    }
    this.updatePendingWorkState(documentId);
  }

  private onWordTyped(documentId: string, index: number, _enqueuedWord: string): void {
    const state = this.states.get(documentId);
    if (!state) return;

    // Use the current rawWord — translation may have refined since enqueue
    const currentWord = state.rawWords[index];
    if (currentWord !== undefined) {
      state.commitWord(index, currentWord);
    }
  }

  /** Called when the queue finishes a message's words. Re-enqueues if new words arrived mid-typing. */
  private onMessageComplete(documentId: string): void {
    const state = this.states.get(documentId);
    if (!state) return;

    if (!state.isFullyCommitted()) {
      const uncommitted = state.getUncommittedWords();
      if (uncommitted.length > 0) {
        this.queue.enqueue(documentId, uncommitted);
        return;
      }
    }

    state.setTyping(false);
    if (state.isFinal && state.isFullyCommitted()) {
      state.freeze();
    }

    this.updatePendingWorkState(documentId);
  }

  // --- Helpers ---

  private getOrCreateState(documentId: string): MessageState {
    let state = this.states.get(documentId);
    if (!state) {
      state = new MessageState(documentId);
      this.states.set(documentId, state);
    }
    return state;
  }

  private cancelPendingRemoval(documentId: string): void {
    const timeout = this.pendingRemovals.get(documentId);
    if (timeout) {
      clearTimeout(timeout);
      this.pendingRemovals.delete(documentId);
    }
  }

  /**
   * Schedules a delayed enqueue of uncommitted words for a released, non-final message.
   * Uses throttle behavior: won't start a new timer if one is already running.
   * This gives translations time to stabilize before words enter the typing queue.
   */
  private schedulePostReleaseEnqueue(documentId: string): void {
    if (this.currentBufferMs <= 0) {
      const state = this.states.get(documentId);
      if (state && !state.isFrozen) {
        const uncommitted = state.getUncommittedWords();
        if (uncommitted.length > 0) {
          if (!state.isTyping) state.setTyping(true);
          this.queue.enqueue(documentId, uncommitted);
        }
      }
      return;
    }

    if (this.postReleaseTimers.has(documentId)) return;

    this.postReleaseTimers.set(documentId, setTimeout(() => {
      this.postReleaseTimers.delete(documentId);
      const state = this.states.get(documentId);
      if (!state || state.isFrozen) return;

      const uncommitted = state.getUncommittedWords();
      if (uncommitted.length > 0) {
        if (!state.isTyping) state.setTyping(true);
        this.queue.enqueue(documentId, uncommitted);
      }
      this.updatePendingWorkState(documentId);
    }, POST_RELEASE_DELAY_MS));
  }

  private flushPostReleaseTimer(documentId: string): void {
    const timer = this.postReleaseTimers.get(documentId);
    if (timer) {
      clearTimeout(timer);
      this.postReleaseTimers.delete(documentId);
    }
  }

  /**
   * Updates the pending work stream and signal for a document ID.
   * Emits only if the state has changed.
   */
  private updatePendingWorkState(documentId: string): void {
    const hasPending = this.hasPendingWork(documentId);

    const subject = this.pendingWorkStreams.get(documentId);
    if (subject && subject.value !== hasPending) {
      subject.next(hasPending);
    }
  }

  /** Remembers a removed message's typed text (bounded LRU) for quick restore. */
  private cacheCommittedText(documentId: string, text: string, isFinal: boolean): void {
    this.committedTextCache.delete(documentId);
    this.committedTextCache.set(documentId, { text, isFinal });
    if (this.committedTextCache.size > this.MAX_CACHED_COMMITTED) {
      const oldest = this.committedTextCache.keys().next().value;
      if (oldest !== undefined) this.committedTextCache.delete(oldest);
    }
  }

  /**
   * If a freshly-(re)created message has cached typed text and the incoming text
   * still begins with it, commit that text instantly instead of re-typing.
   */
  private restoreCommittedText(documentId: string, incomingText: string): void {
    const cached = this.committedTextCache.get(documentId);
    if (!cached) return;

    const state = this.states.get(documentId);
    if (!state || state.isFrozen || state.committedCount > 0) return;

    if (!incomingText.startsWith(cached.text)) {
      this.committedTextCache.delete(documentId);
      return;
    }

    const words = parseWords(cached.text);
    state.updateRawWords(words);
    state.markReleased();
    for (let i = 0; i < words.length; i++) state.commitWord(i, words[i]);
    if (cached.isFinal && parseWords(incomingText).length === words.length) {
      state.markFinal();
      state.freeze();
    }
    this.committedTextCache.delete(documentId);
    this.updatePendingWorkState(documentId);
  }
}
